import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import { DeleteIcon, EditIcon } from 'assets/icons';
import chartColorPalette from 'assets/styles/chartColorPalette';
import { ContactButton } from 'components/UI/Buttons';
import { type FC, useState } from 'react';
import { useDeleteSectionMutation, useUpdateSectionMutation } from 'services/api/reports.api';
import type { ChartDataPoint, ReportSection } from 'services/types/report';
import { NAVY, RADIUS_CARD, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

interface Props {
  section: ReportSection;
  reportId: number;
  isEditMode: boolean;
}

// Funnel per design system §19.8: descending horizontal bars (not trapezoids),
// direct label with absolute value + conversion rate vs. the previous step,
// and the step with the largest drop highlighted in YELLOW.
const FunnelChart: FC<{ data: ChartDataPoint[]; height: number }> = ({ data, height }) => {
  const maxValue = data.reduce((acc, d) => (d.value > acc ? d.value : acc), 0);

  // Ratio of each step vs. the previous one; the smallest ratio (< 1) is the largest drop.
  const stepRatios = data.map((d, i) => {
    const prev = i > 0 ? data[i - 1].value : null;
    return prev && prev > 0 ? d.value / prev : null;
  });
  let biggestDropIndex = -1;
  let smallestRatio = 1;
  stepRatios.forEach((ratio, i) => {
    if (ratio !== null && ratio < smallestRatio) {
      smallestRatio = ratio;
      biggestDropIndex = i;
    }
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, minHeight: height, py: 1 }}>
      {data.map((point, index) => {
        const barWidth = maxValue > 0 ? Math.max((point.value / maxValue) * 100, 2) : 0;
        const isHighlighted = index === biggestDropIndex;
        const ratio = stepRatios[index];
        return (
          <Box key={point.label}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
                {point.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
                  {point.value.toLocaleString('es-CL')}
                </Typography>
                {ratio !== null && (
                  <Typography variant="caption" sx={{ color: alpha(NAVY, 0.5) }}>
                    {Math.round(ratio * 100)}% del paso anterior
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ width: '100%', bgcolor: alpha(NAVY, 0.06), borderRadius: RADIUS_COIN, overflow: 'hidden' }}>
              <Box
                sx={{
                  width: `${barWidth}%`,
                  height: 28,
                  bgcolor: isHighlighted ? YELLOW : alpha(NAVY, 0.85),
                  borderRadius: RADIUS_COIN,
                  transition: 'width 0.3s ease-in-out',
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const cardSx = {
  p: 3,
  height: '100%',
  position: 'relative',
  bgcolor: SURFACE_CONTAINER_LOWEST,
  borderRadius: RADIUS_CARD,
  boxShadow: SHADOW,
} as const;

const hiddenCardSx = {
  ...cardSx,
  opacity: 0.5,
  border: `2px dashed ${alpha(NAVY, 0.2)}`,
} as const;

const editBtnSx = {
  color: alpha(NAVY, 0.55),
  transition: 'all 0.15s ease-in-out',
  '&:hover': { bgcolor: alpha(NAVY, 0.06) },
} as const;

const ChartSection: FC<Props> = ({ section, reportId, isEditMode }: Props) => {
  const [updateSection] = useUpdateSectionMutation();
  const [deleteSection] = useDeleteSectionMutation();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editWidth, setEditWidth] = useState(section.width);
  const [editHeight, setEditHeight] = useState(section.height);

  const { chartData } = section;

  const handleOpenEdit = () => {
    setEditWidth(section.width);
    setEditHeight(section.height);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    updateSection({ reportId, sectionId: section.id, width: editWidth, height: editHeight })
      .unwrap()
      .then(() => setEditDialogOpen(false))
      .catch(console.error);
  };

  const handleToggleVisibility = () => {
    updateSection({ reportId, sectionId: section.id, isHidden: !section.isHidden }).catch(console.error);
  };

  const handleDelete = () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta sección?')) return;
    deleteSection({ reportId, sectionId: section.id }).catch(console.error);
  };

  const editControls = isEditMode && (
    <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, zIndex: 1 }}>
      <Tooltip title={section.isHidden ? 'Mostrar sección' : 'Ocultar sección'}>
        <IconButton size="small" onClick={handleToggleVisibility} sx={editBtnSx}>
          {section.isHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar tamaño">
        <IconButton size="small" onClick={handleOpenEdit} sx={editBtnSx}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar sección">
        <IconButton
          size="small"
          onClick={handleDelete}
          sx={{ ...editBtnSx, '&:hover': { bgcolor: 'error.light', color: 'error.dark' } }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (!chartData?.data || chartData.data.length === 0) {
    return (
      <Box sx={section.isHidden ? hiddenCardSx : cardSx}>
        {editControls}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: NAVY }}>
          {section.title}
        </Typography>
        <Typography sx={{ color: alpha(NAVY, 0.5) }}>No hay datos disponibles para mostrar.</Typography>
      </Box>
    );
  }

  const longestLabel = chartData.data.reduce((acc, data) => (data.label.length > acc ? data.label.length : acc), 5);
  const hasSecondaryValues = chartData.data.some((item) => item.secondaryValue !== undefined);

  const barAndLineSeries: Array<{ data: number[]; label: string; yAxisId?: string }> = [
    {
      data: chartData.data.map((d) => d.value),
      label: chartData.valueLabel || chartData.yAxisLabel,
      ...(hasSecondaryValues ? { yAxisId: 'left' } : {}),
    },
  ];

  const scatterSeries = [
    {
      data: chartData.data.map((d) => ({ id: d.label, x: Number(d.label), y: d.value })),
      label: chartData.valueLabel || chartData.yAxisLabel,
    },
  ];

  if (hasSecondaryValues) {
    barAndLineSeries.push({
      data: chartData.data.map((d) => d.secondaryValue ?? 0),
      label: chartData.secondaryValueLabel || 'Valor secundario',
      yAxisId: 'right',
    });
    scatterSeries.push({
      data: chartData.data.map((d) => ({ id: d.label, x: Number(d.label), y: d.secondaryValue ?? 0 })),
      label: chartData.secondaryValueLabel || 'Valor secundario',
    });
  }

  let baseHeight = 240;
  if (section.height === 2) baseHeight = 360;
  else if (section.height === 3) baseHeight = 480;
  else if (section.height === 4) baseHeight = 600;

  const dualYAxis = [
    { id: 'left', label: chartData.yAxisLabel },
    { id: 'right', label: chartData.secondaryValueLabel || 'Valor secundario', position: 'right' as const },
  ];
  const singleYAxis = [{ label: chartData.yAxisLabel }];

  const xAxis = [
    {
      data: chartData.data.map((d) => d.label),
      scaleType: 'band' as const,
      height: 'auto' as const,
      tickLabelStyle: longestLabel > 8 ? { angle: 45, textAnchor: 'start' as const, fontSize: 10 } : undefined,
    },
  ];

  return (
    <Box sx={section.isHidden ? hiddenCardSx : cardSx}>
      {editControls}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: NAVY, pr: isEditMode ? 8 : 0 }}>
        {section.title}
      </Typography>
      {section.subtitle && (
        <Typography variant="body2" gutterBottom sx={{ color: alpha(NAVY, 0.6) }}>
          {section.subtitle}
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        {section.chartType === 'bar' && (
          <BarChart
            colors={chartColorPalette}
            series={barAndLineSeries}
            height={baseHeight}
            xAxis={xAxis}
            yAxis={hasSecondaryValues ? dualYAxis : singleYAxis}
          />
        )}
        {section.chartType === 'line' && (
          <LineChart
            colors={chartColorPalette}
            series={barAndLineSeries}
            height={baseHeight}
            xAxis={xAxis}
            yAxis={hasSecondaryValues ? dualYAxis : singleYAxis}
          />
        )}
        {section.chartType === 'pie' && (
          <PieChart
            colors={chartColorPalette}
            skipAnimation
            series={[
              {
                data: chartData.data.map((d) => ({ id: d.label, label: d.label, value: d.value })),
                innerRadius: 50,
              },
            ]}
            height={baseHeight}
            margin={{ right: 150 }}
            slotProps={{ legend: { direction: 'vertical', position: { vertical: 'top', horizontal: 'end' } } }}
          />
        )}
        {section.chartType === 'scatter' && (
          <ScatterChart
            colors={chartColorPalette}
            series={scatterSeries}
            height={baseHeight}
            xAxis={[{ label: chartData.xAxisLabel }]}
            yAxis={[{ label: chartData.yAxisLabel }]}
          />
        )}
        {section.chartType === 'funnel' && <FunnelChart data={chartData.data} height={baseHeight} />}
      </Box>

      {['line', 'bar'].includes(section.chartType || '') && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant="body2" sx={{ color: alpha(NAVY, 0.5) }}>
            {chartData.xAxisLabel}
          </Typography>
        </Box>
      )}

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: NAVY, fontWeight: 600, fontFamily: 'Jost, sans-serif' }}>
          Editar tamaño del gráfico
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom sx={{ color: alpha(NAVY, 0.6) }}>
              Ancho: {Math.round((editWidth / 12) * 100)}%
            </Typography>
            <Slider
              value={editWidth}
              onChange={(_, v) => setEditWidth(v as number)}
              min={1}
              max={12}
              step={1}
              marks={[
                { value: 3, label: '25%' },
                { value: 6, label: '50%' },
                { value: 9, label: '75%' },
                { value: 12, label: '100%' },
              ]}
              valueLabelDisplay="auto"
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom sx={{ color: alpha(NAVY, 0.6) }}>
              Alto: {editHeight}
            </Typography>
            <Slider
              value={editHeight}
              onChange={(_, v) => setEditHeight(v as number)}
              min={1}
              max={4}
              step={1}
              marks={[
                { value: 1, label: '1' },
                { value: 2, label: '2' },
                { value: 3, label: '3' },
                { value: 4, label: '4' },
              ]}
              valueLabelDisplay="auto"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ color: alpha(NAVY, 0.6), borderRadius: RADIUS_COIN, '&:hover': { bgcolor: alpha(NAVY, 0.06) } }}
          >
            Cancelar
          </Button>
          <ContactButton onClick={handleSaveEdit} sx={{ height: 36 }}>
            Guardar
          </ContactButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChartSection;
